import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon, MagnifyingGlassIcon, Squares2X2Icon, ListBulletIcon,
  BuildingOfficeIcon, UserIcon, BanknotesIcon, ClipboardDocumentListIcon,
  ExclamationCircleIcon, SparklesIcon, ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { PROSPECT_STAGES, STAGE_CONFIG, STAGE_LABELS } from '../../lib/validation';
import type { ProspectStage } from '../../lib/validation';
import { toast } from 'react-hot-toast';
import ProspectForm from './ProspectForm';
import ProspectDetailsDrawer from './ProspectDetailsDrawer';

interface ProspectContact {
  id: string;
  full_name: string;
  title?: string;
  email?: string;
  phone?: string;
  is_primary: boolean;
}

interface ProspectTask {
  id: string;
  is_completed: boolean;
  due_date?: string;
  priority: string;
}

interface ProspectCommitment {
  id: string;
  tier_interest?: string;
  amount_pledged?: number;
  probability_pct?: number;
  status: string;
}

interface Prospect {
  id: string;
  company_name: string;
  industry?: string;
  company_size?: string;
  website?: string;
  stage: string;
  source?: string;
  source_detail?: string;
  notes?: string;
  lost_reason?: string;
  partner_id?: string;
  tournament_id?: string;
  logo_url?: string;
  created_at: string;
  prospect_contacts: ProspectContact[];
  prospect_tasks: ProspectTask[];
  prospect_commitments: ProspectCommitment[];
}

interface ProspectsPageProps {
  tournamentId: string;
}

function isOverdue(dueDate?: string) {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
}

function ProspectCard({ prospect, onClick, onStageChange }: {
  prospect: Prospect;
  onClick: () => void;
  onStageChange: (id: string, stage: ProspectStage) => void;
}) {
  const primaryContact = prospect.prospect_contacts.find(c => c.is_primary) || prospect.prospect_contacts[0];
  const latestCommitment = prospect.prospect_commitments[0];
  const openTasks = prospect.prospect_tasks.filter(t => !t.is_completed);
  const overdueTasks = openTasks.filter(t => isOverdue(t.due_date));
  const stageConfig = STAGE_CONFIG[prospect.stage as ProspectStage];

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('prospectId', prospect.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      draggable
      onDragStart={handleDragStart}
      onClick={onClick}
      className={`bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer select-none border-l-4 ${stageConfig?.border || 'border-l-gray-300'} ${prospect.stage === 'won' ? 'bg-green-50/30' : ''}`}
    >
      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-golf-gray truncate">{prospect.company_name}</p>
            {prospect.industry && <p className="text-xs text-gray-500 truncate">{prospect.industry}</p>}
          </div>
          {overdueTasks.length > 0 && (
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
              {overdueTasks.length}
            </span>
          )}
          {prospect.partner_id && (
            <SparklesIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          )}
        </div>

        {primaryContact && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <UserIcon className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{primaryContact.full_name}{primaryContact.title ? ` · ${primaryContact.title}` : ''}</span>
          </div>
        )}

        {latestCommitment && (
          <div className="flex items-center gap-2">
            {latestCommitment.amount_pledged ? (
              <span className="text-xs font-semibold text-golf-orange">₦{(latestCommitment.amount_pledged / 1000000).toFixed(1)}M</span>
            ) : null}
            {latestCommitment.tier_interest && (
              <span className="text-xs text-gray-500 truncate">{latestCommitment.tier_interest}</span>
            )}
            {latestCommitment.probability_pct !== undefined && (
              <span className="ml-auto text-xs text-gray-400">{latestCommitment.probability_pct}%</span>
            )}
          </div>
        )}

        {openTasks.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <ClipboardDocumentListIcon className="w-3 h-3" />
            <span>{openTasks.length} task{openTasks.length !== 1 ? 's' : ''}</span>
            {overdueTasks.length > 0 && (
              <span className="flex items-center gap-0.5 text-red-500 ml-1">
                <ExclamationCircleIcon className="w-3 h-3" />
                {overdueTasks.length} overdue
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function KanbanColumn({ stage, prospects, onCardClick, onStageChange, onAddInStage, isDragOver, onDragOver, onDrop, onDragLeave }: {
  stage: ProspectStage;
  prospects: Prospect[];
  onCardClick: (p: Prospect) => void;
  onStageChange: (id: string, stage: ProspectStage) => void;
  onAddInStage: (stage: ProspectStage) => void;
  isDragOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, stage: ProspectStage) => void;
  onDragLeave: () => void;
}) {
  const config = STAGE_CONFIG[stage];
  return (
    <div
      className={`flex flex-col min-w-[260px] max-w-[260px] rounded-xl border transition-all ${isDragOver ? 'ring-2 ring-golf-orange border-golf-orange/50 bg-golf-orange/5' : 'border-gray-200 bg-gray-50/50'}`}
      onDragOver={e => { e.preventDefault(); onDragOver(e); }}
      onDrop={e => onDrop(e, stage)}
      onDragLeave={onDragLeave}
    >
      <div className={`px-3 py-2.5 rounded-t-xl border-b border-gray-200 ${config.headerBg}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${config.dot}`} />
            <span className="text-xs font-semibold text-gray-700">{config.label}</span>
          </div>
          <span className="text-xs font-medium text-gray-500 bg-white px-1.5 py-0.5 rounded-full">{prospects.length}</span>
        </div>
      </div>

      <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-320px)] min-h-[100px]">
        <AnimatePresence>
          {prospects.map(p => (
            <ProspectCard key={p.id} prospect={p} onClick={() => onCardClick(p)} onStageChange={onStageChange} />
          ))}
        </AnimatePresence>
      </div>

      {stage !== 'won' && stage !== 'lost' && (
        <div className="p-2 border-t border-gray-200">
          <button onClick={() => onAddInStage(stage)} className="w-full flex items-center justify-center gap-1 py-1.5 text-xs text-gray-400 hover:text-golf-orange hover:bg-golf-orange/5 rounded-lg transition-colors">
            <PlusIcon className="w-3.5 h-3.5" />
            Add
          </button>
        </div>
      )}
    </div>
  );
}

export default function ProspectsPage({ tournamentId }: ProspectsPageProps) {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProspect, setSelectedProspect] = useState<Prospect | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingProspect, setEditingProspect] = useState<Prospect | null>(null);
  const [defaultStage, setDefaultStage] = useState<ProspectStage>('lead');
  const [dragOverStage, setDragOverStage] = useState<ProspectStage | null>(null);
  const [importing, setImporting] = useState(false);

  const fetchProspects = useCallback(async () => {
    setLoading(true);
    const query = supabase
      .from('prospects')
      .select(`*, prospect_contacts(id, full_name, title, email, phone, is_primary), prospect_tasks(id, is_completed, due_date, priority), prospect_commitments(id, tier_interest, amount_pledged, probability_pct, status)`)
      .order('created_at', { ascending: false });

    if (tournamentId) {
      query.eq('tournament_id', tournamentId);
    }

    const { data, error } = await query;
    if (error) toast.error('Failed to load prospects');
    else setProspects((data || []) as Prospect[]);
    setLoading(false);
  }, [tournamentId]);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);

  const handleImportSponsors = async () => {
    setImporting(true);
    try {
      const { data: partners, error: partnersError } = await supabase
        .from('partners')
        .select('id, company_name, logo_url, website, tier, contact_person, email, phone, contribution, tournament_id, tournaments(name, year)');
      if (partnersError) throw partnersError;
      if (!partners || partners.length === 0) {
        toast('No sponsors found to import.');
        return;
      }

      const { data: existingProspects, error: existingError } = await supabase
        .from('prospects')
        .select('partner_id')
        .not('partner_id', 'is', null);
      if (existingError) throw existingError;

      const linkedPartnerIds = new Set((existingProspects || []).map(p => p.partner_id));
      const toImport = partners.filter(p => !linkedPartnerIds.has(p.id));

      if (toImport.length === 0) {
        toast('All historical sponsors are already in the prospects list.');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();

      let imported = 0;
      for (const partner of toImport) {
        const tournament = (partner as any).tournaments;
        const tournamentLabel = tournament
          ? `${tournament.name}${tournament.year ? ` ${tournament.year}` : ''}`
          : null;
        const sourceDetail = tournamentLabel
          ? `Historical sponsor – ${tournamentLabel}`
          : 'Historical sponsor import';

        const { data: prospect, error: prospectError } = await supabase
          .from('prospects')
          .insert({
            company_name: partner.company_name,
            logo_url: partner.logo_url,
            website: partner.website,
            stage: 'won',
            source: 'other',
            source_detail: sourceDetail,
            notes: `Tier: ${partner.tier}${tournamentLabel ? ` | Tournament: ${tournamentLabel}` : ''}`,
            partner_id: partner.id,
            tournament_id: tournamentId || null,
          })
          .select('id')
          .single();
        if (prospectError || !prospect) continue;

        if (partner.contact_person || partner.email || partner.phone) {
          await supabase.from('prospect_contacts').insert({
            prospect_id: prospect.id,
            full_name: partner.contact_person || partner.company_name,
            email: partner.email || null,
            phone: partner.phone || null,
            is_primary: true,
          });
        }

        if (partner.contribution) {
          await supabase.from('prospect_commitments').insert({
            prospect_id: prospect.id,
            tier_interest: partner.tier,
            amount_pledged: partner.contribution,
            probability_pct: 100,
            status: 'confirmed',
          });
        }

        await supabase.from('prospect_activities').insert({
          prospect_id: prospect.id,
          activity_type: 'note',
          title: tournamentLabel
            ? `Imported from ${tournamentLabel} sponsors`
            : 'Imported from historical sponsors list',
          performed_by: user?.id,
        });

        imported++;
      }

      toast.success(`Imported ${imported} historical sponsor${imported !== 1 ? 's' : ''} as prospects.`);
      fetchProspects();
    } catch {
      toast.error('Failed to import sponsors.');
    } finally {
      setImporting(false);
    }
  };

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return prospects;
    const q = searchTerm.toLowerCase();
    return prospects.filter(p =>
      p.company_name.toLowerCase().includes(q) ||
      p.industry?.toLowerCase().includes(q) ||
      p.prospect_contacts.some(c => c.full_name.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q))
    );
  }, [prospects, searchTerm]);

  const byStage = useMemo(() => {
    const map: Record<ProspectStage, Prospect[]> = {} as Record<ProspectStage, Prospect[]>;
    for (const s of PROSPECT_STAGES) map[s] = [];
    for (const p of filtered) {
      const s = p.stage as ProspectStage;
      if (map[s]) map[s].push(p);
    }
    return map;
  }, [filtered]);

  const stats = useMemo(() => {
    const total = prospects.length;
    const pipelineValue = prospects.reduce((sum, p) => {
      const c = p.prospect_commitments[0];
      return sum + (c?.amount_pledged || 0);
    }, 0);
    const won = prospects.filter(p => p.stage === 'won').length;
    const withProbability = prospects.filter(p => p.prospect_commitments[0]?.probability_pct !== undefined);
    const avgProbability = withProbability.length > 0
      ? Math.round(withProbability.reduce((sum, p) => sum + (p.prospect_commitments[0]?.probability_pct || 0), 0) / withProbability.length)
      : 0;
    return { total, pipelineValue, won, avgProbability };
  }, [prospects]);

  const handleStageChange = async (prospectId: string, newStage: ProspectStage) => {
    const prospect = prospects.find(p => p.id === prospectId);
    if (!prospect || prospect.stage === newStage) return;
    setProspects(prev => prev.map(p => p.id === prospectId ? { ...p, stage: newStage } : p));
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from('prospects').update({ stage: newStage }).eq('id', prospectId);
    if (error) {
      toast.error('Failed to update stage');
      setProspects(prev => prev.map(p => p.id === prospectId ? { ...p, stage: prospect.stage } : p));
      return;
    }
    await supabase.from('prospect_activities').insert([{
      prospect_id: prospectId,
      activity_type: 'stage_change',
      title: `Stage changed to ${STAGE_LABELS[newStage]}`,
      metadata: { from_stage: prospect.stage, to_stage: newStage },
      performed_by: user?.id,
    }]);
  };

  const handleDrop = (e: React.DragEvent, stage: ProspectStage) => {
    e.preventDefault();
    const prospectId = e.dataTransfer.getData('prospectId');
    if (prospectId) handleStageChange(prospectId, stage);
    setDragOverStage(null);
  };

  const openAddForm = (stage: ProspectStage = 'lead') => {
    setDefaultStage(stage);
    setEditingProspect(null);
    setFormOpen(true);
  };

  const openEditForm = (prospect: Prospect) => {
    setEditingProspect(prospect as unknown as Prospect);
    setFormOpen(true);
  };

  const openDrawer = (prospect: Prospect) => {
    setSelectedProspect(prospect);
    setDrawerOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total Prospects', value: stats.total, sub: 'all stages', icon: BuildingOfficeIcon, color: 'text-golf-gray' },
          { label: 'Pipeline Value', value: `₦${(stats.pipelineValue / 1000000).toFixed(1)}M`, sub: 'pledged total', icon: BanknotesIcon, color: 'text-golf-orange' },
          { label: 'Won', value: stats.won, sub: 'activated partners', icon: SparklesIcon, color: 'text-emerald-600' },
          { label: 'Avg Probability', value: `${stats.avgProbability}%`, sub: 'of commitment', icon: UserIcon, color: 'text-blue-600' },
        ].map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center">
                <Icon className={`w-5 h-5 ${color}`} />
              </div>
              <div>
                <p className="text-xs text-gray-500">{label}</p>
                <p className={`text-lg font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-400">{sub}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search prospects..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange"
          />
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button onClick={() => setViewMode('kanban')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'kanban' ? 'bg-white shadow text-golf-gray' : 'text-gray-400 hover:text-gray-600'}`}>
            <Squares2X2Icon className="w-4 h-4" />
          </button>
          <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow text-golf-gray' : 'text-gray-400 hover:text-gray-600'}`}>
            <ListBulletIcon className="w-4 h-4" />
          </button>
        </div>
        <button
          onClick={handleImportSponsors}
          disabled={importing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          {importing ? 'Importing...' : 'Import Historical Sponsors'}
        </button>
        <button onClick={() => openAddForm()} className="flex items-center gap-2 px-4 py-2 bg-golf-orange text-white text-sm font-medium rounded-lg hover:bg-golf-orange/90 transition-colors">
          <PlusIcon className="w-4 h-4" />
          Add Prospect
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-golf-orange border-t-transparent rounded-full animate-spin" />
        </div>
      ) : viewMode === 'kanban' ? (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max">
            {PROSPECT_STAGES.map(stage => (
              <KanbanColumn
                key={stage}
                stage={stage}
                prospects={byStage[stage] || []}
                onCardClick={openDrawer}
                onStageChange={handleStageChange}
                onAddInStage={openAddForm}
                isDragOver={dragOverStage === stage}
                onDragOver={() => setDragOverStage(stage)}
                onDrop={handleDrop}
                onDragLeave={() => setDragOverStage(null)}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Stage</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Commitment</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Tasks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-gray-400">
                    <BuildingOfficeIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p>No prospects found</p>
                  </td>
                </tr>
              ) : filtered.map(p => {
                const stageConfig = STAGE_CONFIG[p.stage as ProspectStage];
                const primaryContact = p.prospect_contacts.find(c => c.is_primary) || p.prospect_contacts[0];
                const commitment = p.prospect_commitments[0];
                const openTasks = p.prospect_tasks.filter(t => !t.is_completed);
                const overdueTasks = openTasks.filter(t => isOverdue(t.due_date));
                return (
                  <tr key={p.id} onClick={() => openDrawer(p)} className="hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-golf-gray">{p.company_name}</p>
                        {p.industry && <p className="text-xs text-gray-500">{p.industry}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${stageConfig?.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${stageConfig?.dot}`} />
                        {stageConfig?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {primaryContact ? (
                        <div>
                          <p className="text-golf-gray">{primaryContact.full_name}</p>
                          {primaryContact.email && <p className="text-xs text-gray-500">{primaryContact.email}</p>}
                        </div>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {commitment ? (
                        <div>
                          {commitment.tier_interest && <p className="text-xs text-gray-600">{commitment.tier_interest}</p>}
                          {commitment.amount_pledged && <p className="font-medium text-golf-orange">₦{commitment.amount_pledged.toLocaleString()}</p>}
                        </div>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {openTasks.length > 0 ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-600">{openTasks.length}</span>
                          {overdueTasks.length > 0 && (
                            <span className="flex items-center gap-0.5 text-xs text-red-500">
                              <ExclamationCircleIcon className="w-3.5 h-3.5" />
                              {overdueTasks.length}
                            </span>
                          )}
                        </div>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ProspectForm
        isOpen={formOpen}
        onClose={() => { setFormOpen(false); setEditingProspect(null); }}
        onSuccess={fetchProspects}
        tournamentId={tournamentId}
        prospect={editingProspect}
      />

      <ProspectDetailsDrawer
        prospectId={selectedProspect?.id || null}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onRefresh={fetchProspects}
        onEdit={(p) => { setDrawerOpen(false); openEditForm(p as unknown as Prospect); }}
      />
    </div>
  );
}
